import { useState, useMemo, useEffect } from "react";
import Chart from "react-apexcharts";
import Grid from "@mui/material/Grid";
import Popover from "@mui/material/Popover";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import { IoDocumentText, IoLocation, IoBriefcase, IoPeople, IoInformationCircleOutline } from "react-icons/io5";

import DashboardLayout from "components/layout/DashboardLayout";
import DashboardNavbar from "components/navigation/DashboardNavbar";
import GlassCard from "components/GlassCard";
import GlassCardLoader from "components/GlassCard/GlassCardLoader";
import MiniStatCard, { MiniStatCardSkeleton } from "components/MiniStatCard";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import VuiButton from "components/ui/VuiButton";
import PorterTable from "components/PorterTable";
import StandardLoadingIndicator from "components/StandardLoadingIndicator";
import TableHeaderWithFilters from "components/TableFilters/TableHeaderWithFilters";
import VuiPaginationControls from "components/ui/VuiPagination/custom/VuiCustomPagination";
import { generateMonthOptions } from "utils/dateFilterUtils";
import { PAGINATION, POPOVER, FILTER_DROPDOWN } from "utils/designTokens";
import { useAnalytics } from "context/AnalyticsContext";
import { analyticsService } from "services/trackingService";

import { useExpansionTrendsData } from "./data/useExpansionTrendsData";

// ── Constants ─────────────────────────────────────────────────────────────────

const MARKET_TRENDS_ID = "market_trends";

const MODAL_STYLE = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "1200px",
  bgcolor: "rgba(17, 17, 30, 0.97)",
  backdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "20px",
  p: 3,
  display: "flex",
  flexDirection: "column",
  maxHeight: "85vh",
  outline: "none",
};

// ── Label maps ────────────────────────────────────────────────────────────────

const FUNCTION_LABELS = {
  CLINICAL:                 "Clinical",
  REGULATORY_QUALITY:       "Regulatory & Quality",
  ENGINEERING:              "Engineering / R&D",
  SALES_MARKETING:          "Sales & Marketing",
  OPERATIONS_MANUFACTURING: "Operations & Mfg.",
  MANAGEMENT:               "Management",
  FINANCE_HR_LEGAL:         "Finance / Legal / HR",
  OTHER:                    "Other",
};

const SENIORITY_LABELS = {
  EXECUTIVE: "Executive",
  SENIOR:    "Senior",
  MID:       "Mid-Level",
  JUNIOR:    "Junior",
  INTERN:    "Intern",
};

const FUNCTION_COLORS   = ["#2CD9FF", "#4ECDC4", "#0075FF", "#FF6B6B", "#F7B731", "#96CEB4", "#A78BFA", "#888"];
const SENIORITY_COLORS  = ["#FF6B6B", "#F7B731", "#0075FF", "#2CD9FF", "#888"];
const WORKTYPE_COLORS   = ["#0075FF", "#2CD9FF", "#4ECDC4", "#96CEB4"];
const VELOCITY_COLORS   = ["#0075FF", "#2CD9FF", "#4ECDC4", "#FF6B6B", "#F7B731", "#96CEB4", "#A78BFA", "#F97316"];

const ROWS_PER_PAGE_DEFAULT = 10;

// ── Chart builders ────────────────────────────────────────────────────────────

const BASE_TOOLTIP = { style: { fontSize: "11px", fontFamily: "Sora" }, theme: "dark" };
const BASE_LEGEND  = {
  show: true, position: "bottom",
  labels: { colors: "#c8cfca" },
  fontFamily: "Sora", fontSize: "11px",
  markers: { size: 6, shape: "circle" },
  itemMargin: { horizontal: 10, vertical: 4 },
};

function donutOptions(labels, colors, legendOverrides = {}) {
  return {
    chart: { background: "transparent", toolbar: { show: false } },
    labels, colors,
    tooltip: BASE_TOOLTIP,
    legend: { ...BASE_LEGEND, ...legendOverrides },
    dataLabels: {
      enabled: true,
      style: { fontSize: "10px", fontFamily: "Sora", colors: ["#fff"] },
      dropShadow: { enabled: false },
    },
    plotOptions: { pie: { donut: { size: "60%" } } },
    stroke: { show: false },
  };
}

function horizontalBarOptions(categories) {
  return {
    chart: { background: "transparent", toolbar: { show: false } },
    tooltip: BASE_TOOLTIP,
    xaxis: { categories, labels: { style: { colors: "#c8cfca", fontSize: "10px", fontFamily: "Sora" } } },
    yaxis: { labels: { style: { colors: "#c8cfca", fontSize: "10px", fontFamily: "Sora" } } },
    grid: { borderColor: "#56577A", strokeDashArray: 5 },
    fill: { colors: ["#0075FF"] },
    dataLabels: { enabled: false },
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
  };
}

function velocityBarOptions(categories) {
  return {
    chart: { background: "transparent", toolbar: { show: false } },
    tooltip: BASE_TOOLTIP,
    xaxis: { categories, labels: { rotate: -30, style: { colors: "#c8cfca", fontSize: "10px", fontFamily: "Sora" } } },
    yaxis: { labels: { style: { colors: "#c8cfca", fontSize: "10px", fontFamily: "Sora" } } },
    grid: { borderColor: "#56577A", strokeDashArray: 5 },
    fill: { colors: ["#0075FF"] },
    dataLabels: { enabled: false },
    plotOptions: { bar: { borderRadius: 4, columnWidth: "40%" } },
  };
}

function stackedVelocityOptions(categories) {
  return {
    chart: { type: "bar", stacked: true, background: "transparent", toolbar: { show: false } },
    colors: VELOCITY_COLORS,
    xaxis: {
      categories,
      labels: { rotate: -30, style: { colors: "#c8cfca", fontSize: "10px", fontFamily: "Sora" } },
    },
    yaxis: { labels: { style: { colors: "#c8cfca", fontSize: "10px", fontFamily: "Sora" } } },
    grid: { borderColor: "#56577A", strokeDashArray: 5 },
    tooltip: BASE_TOOLTIP,
    legend: BASE_LEGEND,
    dataLabels: { enabled: false },
    plotOptions: { bar: { borderRadius: 0, columnWidth: "55%" } },
    fill: { opacity: 1 },
  };
}

// ── Shared empty-card message ─────────────────────────────────────────────────

function EmptyCardMessage({ competitorName, height = "300px" }) {
  return (
    <VuiBox display="flex" alignItems="center" justifyContent="center" height={height}>
      <VuiTypography variant="body2" color="text" sx={{ opacity: 0.45, textAlign: "center" }}>
        {competitorName} isn&apos;t hiring right now.
      </VuiTypography>
    </VuiBox>
  );
}

// ── Chart card with unified loading/empty/data states ────────────────────────

function ChartCard({ title, tooltipText, loading, hasValues, children, competitorName, height = "300px" }) {
  return (
    <GlassCard>
      <VuiBox pb={1} display="flex" alignItems="center" gap={0.5}>
        <VuiTypography variant="h6" color="white" fontWeight="medium">{title}</VuiTypography>
        {tooltipText && (
          <Tooltip
            title={
              <VuiTypography variant="caption" color="white" sx={{ fontSize: "11px", fontFamily: "Sora, sans-serif" }}>
                {tooltipText}
              </VuiTypography>
            }
            placement="top"
            arrow
          >
            <span style={{ display: "inline-flex", cursor: "help", opacity: 0.55 }}>
              <IoInformationCircleOutline size="14px" color="white" />
            </span>
          </Tooltip>
        )}
      </VuiBox>
      {loading ? (
        <StandardLoadingIndicator containerHeight={height} />
      ) : !hasValues ? (
        <EmptyCardMessage competitorName={competitorName} height={height} />
      ) : (
        children
      )}
    </GlassCard>
  );
}

// ── Expansion Insights card (LLM, pre-computed) ───────────────────────────────

function ExpansionInsightsCard({ intelligence, loading, error, label = "Expansion Insights" }) {
  if (error) return null;

  return (
    <GlassCard sx={{ mb: 3 }}>
      <VuiBox>
        <VuiBox display="flex" alignItems="baseline" gap={1.5} mb={1.5}>
          <VuiTypography
            variant="h6"
            fontWeight="bold"
            sx={{
              background: "linear-gradient(135deg, #A78BFA 0%, #ffffff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {label}
          </VuiTypography>
          <VuiTypography variant="caption" color="text" sx={{ opacity: 0.4, fontSize: "0.7rem" }}>
            based on last monthly snapshot
          </VuiTypography>
        </VuiBox>

        {loading ? (
          <GlassCardLoader />
        ) : !intelligence ? (
          <VuiTypography variant="body2" color="text" sx={{ opacity: 0.5 }}>
            No insights available yet. They are computed automatically after each monthly data refresh.
          </VuiTypography>
        ) : (
          <VuiBox sx={{ maxHeight: 320, overflowY: "auto", pr: 0.5 }}>
            <VuiTypography variant="body2" color="text" sx={{ lineHeight: 1.6 }}>
              {intelligence.strategicSummary}
            </VuiTypography>

            {intelligence.keySignals?.length > 0 && (
              <VuiBox mt={2}>
                <VuiTypography variant="button" color="white" fontWeight="medium" sx={{ mb: 1, display: "block" }}>
                  Key Signals
                </VuiTypography>
                <VuiBox display="flex" flexDirection="column" gap={0.75}>
                  {intelligence.keySignals.map((signal, i) => (
                    <VuiBox key={i} display="flex" alignItems="flex-start" gap={1}>
              <VuiBox
                sx={{
                  flexShrink: 0, mt: 0.3,
                  width: 20, height: 20,
                  borderRadius: "50%",
                  background: "rgba(78,205,196,0.2)",
                  border: "1px solid rgba(78,205,196,0.5)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <VuiTypography variant="caption" fontWeight="bold" sx={{ color: "#4ECDC4", lineHeight: 1, fontSize: "0.65rem" }}>
                  {i + 1}
                </VuiTypography>
              </VuiBox>
                      <VuiTypography variant="body2" color="text" sx={{ lineHeight: 1.5 }}>
                        {signal}
                      </VuiTypography>
                    </VuiBox>
                  ))}
                </VuiBox>
              </VuiBox>
            )}
          </VuiBox>
        )}
      </VuiBox>
    </GlassCard>
  );
}

// ── Market velocity stacked bar chart ─────────────────────────────────────────

function MarketVelocityChart({ velocityData, loading, height = "320px" }) {
  const allMonths = useMemo(() => {
    if (!velocityData?.competitors?.length) return [];
    return [...new Set(
      velocityData.competitors.flatMap((c) => c.monthlyData.map((m) => m.month))
    )].sort();
  }, [velocityData]);

  const series = useMemo(() => {
    if (!velocityData?.competitors?.length) return [];
    return velocityData.competitors.map((c) => ({
      name: c.companyName,
      data: allMonths.map((month) => {
        const md = c.monthlyData.find((m) => m.month === month);
        return md ? Number(md.count) : 0;
      }),
    }));
  }, [velocityData, allMonths]);

  if (loading) return <StandardLoadingIndicator containerHeight={height} />;
  if (!velocityData || !series.length) {
    return <EmptyCardMessage competitorName="The market" height={height} />;
  }

  return (
    <VuiBox height={height}>
      <Chart
        key={JSON.stringify(allMonths)}
        options={stackedVelocityOptions(allMonths)}
        series={series}
        type="bar"
        width="100%"
        height="100%"
      />
    </VuiBox>
  );
}

// ── Filter dropdown helpers ───────────────────────────────────────────────────

const dropdownTriggerSx = {
  display: "flex", alignItems: "center",
  p: "8px 12px", height: "38px",
  background: FILTER_DROPDOWN.background,
  backdropFilter: FILTER_DROPDOWN.backdropFilter,
  border: FILTER_DROPDOWN.border,
  borderRadius: FILTER_DROPDOWN.borderRadius,
  cursor: "pointer", transition: "all 0.2s ease",
  "&:hover": { background: FILTER_DROPDOWN.itemHover, boxShadow: "0 2px 5px rgba(0,0,0,0.2)" },
};

const menuItemSx = {
  color: FILTER_DROPDOWN.textColor,
  borderRadius: "8px", margin: "4px 0", padding: "8px 12px", fontSize: "13px",
  "&:hover": { backgroundColor: FILTER_DROPDOWN.itemHover },
  "&.Mui-selected": {
    color: "#fff", backgroundColor: FILTER_DROPDOWN.selectedBg,
    "&:hover": { backgroundColor: FILTER_DROPDOWN.selectedHover },
  },
};

const menuPaperProps = {
  sx: {
    backgroundColor: FILTER_DROPDOWN.background,
    backdropFilter: FILTER_DROPDOWN.backdropFilter,
    border: FILTER_DROPDOWN.border,
    borderRadius: FILTER_DROPDOWN.borderRadius,
    width: "230px", padding: "8px", maxHeight: "300px",
  },
};

const menuAnchorProps = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
};

// ── Job Titles filter panel ───────────────────────────────────────────────────

function JobTitlesFilterPanel({ filters, onFilterChange, onClose, countries, selectedPresetKey }) {
  const [deptAnchor,    setDeptAnchor]    = useState(null);
  const [countryAnchor, setCountryAnchor] = useState(null);
  const [monthAnchor,   setMonthAnchor]   = useState(null);

  const availableMonths = useMemo(() => generateMonthOptions(selectedPresetKey), [selectedPresetKey]);
  const hasActive = !!(filters.department || filters.country || filters.month);

  const getDeptLabel    = () => FUNCTION_LABELS[filters.department] || "All Departments";
  const getCountryLabel = () => filters.country || "All Countries";
  const getMonthLabel   = () => {
    if (!filters.month) return "All Months";
    const m = availableMonths.find((x) => x.value === filters.month);
    return m ? m.label : "All Months";
  };

  return (
    <VuiBox display="flex" flexDirection="column" gap={2.5} sx={{ minWidth: 220 }}>

      <VuiBox>
        <VuiTypography variant="caption" color="white" fontWeight="medium" mb={1} display="block">
          Department
        </VuiTypography>
        <VuiBox onClick={(e) => setDeptAnchor(e.currentTarget)} sx={dropdownTriggerSx}>
          <VuiTypography variant="button" color={FILTER_DROPDOWN.textColor} fontWeight="medium" sx={{ flexGrow: 1, fontSize: "13px" }}>
            {getDeptLabel()}
          </VuiTypography>
        </VuiBox>
        <Menu anchorEl={deptAnchor} open={Boolean(deptAnchor)} onClose={() => setDeptAnchor(null)} {...menuAnchorProps} PaperProps={menuPaperProps}>
          <MenuItem onClick={() => { onFilterChange("department", ""); setDeptAnchor(null); }} selected={!filters.department} sx={menuItemSx}>All Departments</MenuItem>
          {Object.entries(FUNCTION_LABELS).map(([key, label]) => (
            <MenuItem key={key} onClick={() => { onFilterChange("department", key); setDeptAnchor(null); }} selected={filters.department === key} sx={menuItemSx}>
              {label}
            </MenuItem>
          ))}
        </Menu>
      </VuiBox>

      {countries.length > 0 && (
        <VuiBox>
          <VuiTypography variant="caption" color="white" fontWeight="medium" mb={1} display="block">
            Country
          </VuiTypography>
          <VuiBox onClick={(e) => setCountryAnchor(e.currentTarget)} sx={dropdownTriggerSx}>
            <VuiTypography variant="button" color={FILTER_DROPDOWN.textColor} fontWeight="medium" sx={{ flexGrow: 1, fontSize: "13px" }}>
              {getCountryLabel()}
            </VuiTypography>
          </VuiBox>
          <Menu anchorEl={countryAnchor} open={Boolean(countryAnchor)} onClose={() => setCountryAnchor(null)} {...menuAnchorProps} PaperProps={menuPaperProps}>
            <MenuItem onClick={() => { onFilterChange("country", ""); setCountryAnchor(null); }} selected={!filters.country} sx={menuItemSx}>All Countries</MenuItem>
            {countries.map((c) => (
              <MenuItem key={c} onClick={() => { onFilterChange("country", c); setCountryAnchor(null); }} selected={filters.country === c} sx={menuItemSx}>
                {c}
              </MenuItem>
            ))}
          </Menu>
        </VuiBox>
      )}

      {availableMonths.length > 0 && (
        <VuiBox>
          <VuiTypography variant="caption" color="white" fontWeight="medium" mb={1} display="block">
            Posted Month
          </VuiTypography>
          <VuiBox onClick={(e) => setMonthAnchor(e.currentTarget)} sx={dropdownTriggerSx}>
            <VuiTypography variant="button" color={FILTER_DROPDOWN.textColor} fontWeight="medium" sx={{ flexGrow: 1, fontSize: "13px" }}>
              {getMonthLabel()}
            </VuiTypography>
          </VuiBox>
          <Menu anchorEl={monthAnchor} open={Boolean(monthAnchor)} onClose={() => setMonthAnchor(null)} {...menuAnchorProps} PaperProps={menuPaperProps}>
            <MenuItem onClick={() => { onFilterChange("month", ""); setMonthAnchor(null); }} selected={!filters.month} sx={menuItemSx}>All Months</MenuItem>
            {availableMonths.map((m) => (
              <MenuItem key={m.value} onClick={() => { onFilterChange("month", m.value); setMonthAnchor(null); }} selected={filters.month === m.value} sx={menuItemSx}>
                {m.label}
              </MenuItem>
            ))}
          </Menu>
        </VuiBox>
      )}

      <VuiBox display="flex" justifyContent="space-between" mt={1}>
        <VuiButton color="info" variant={hasActive ? "contained" : "outlined"} onClick={() => onFilterChange("clear")} disabled={!hasActive} size="small">
          Clear All
        </VuiButton>
        <VuiButton color="info" variant="contained" onClick={onClose} size="small">
          Close
        </VuiButton>
      </VuiBox>
    </VuiBox>
  );
}

// ── Job Titles table ──────────────────────────────────────────────────────────

const titlesColumns = [
  { key: "title",       name: "Job Title",    align: "left"                                                            },
  { key: "postings",    name: "Postings",     align: "center", width: "90px"                                           },
  { key: "department",  name: "Department",   align: "left",   width: "160px", render: (v) => FUNCTION_LABELS[v] || v },
  { key: "topLocation", name: "Top Location", align: "left",   width: "190px"                                          },
  { key: "topCountry",  name: "Country",      align: "left",   width: "130px"                                          },
];

function JobTitlesTable({ rows, loading, selectedPresetKey, competitorName }) {
  const [page,         setPage]         = useState(0);
  const [pageSize,     setPageSize]     = useState(ROWS_PER_PAGE_DEFAULT);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [expandOpen,   setExpandOpen]   = useState(false);
  const [filters,      setFilters]      = useState({ department: "", country: "", month: "" });

  const availableCountries = useMemo(
    () => [...new Set(rows.map((r) => r.topCountry).filter((c) => c && c !== "Unknown"))].sort(),
    [rows]
  );

  const filteredRows = useMemo(() => {
    let result = rows;
    if (filters.department) result = result.filter((r) => r.department === filters.department);
    if (filters.country)    result = result.filter((r) => r.topCountry  === filters.country);
    return result;
  }, [rows, filters]);

  const totalElements = filteredRows.length;
  const totalPages    = Math.max(1, Math.ceil(totalElements / pageSize));

  const pagedRows = useMemo(() => {
    const start = page * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const hasActiveFilters = !!(filters.department || filters.country || filters.month);

  const handleFilterChange = (key, value) => {
    if (key === "clear") {
      setFilters({ department: "", country: "", month: "" });
    } else {
      setFilters((prev) => ({ ...prev, [key]: value }));
    }
    setPage(0);
  };

  const handlePageChange     = (newPage) => setPage(newPage);
  const handlePageSizeChange = (newSize) => { setPageSize(newSize); setPage(0); };

  const openFilters = (e) => setFilterAnchorEl(e.currentTarget);
  const closeFilters = () => setFilterAnchorEl(null);

  const tableContent = (tableRows, maxHeight) =>
    loading ? (
      <StandardLoadingIndicator containerHeight={maxHeight} />
    ) : filteredRows.length === 0 ? (
      <EmptyCardMessage competitorName={competitorName} height={maxHeight} />
    ) : (
      <PorterTable
        columns={titlesColumns}
        rows={tableRows}
        maxHeight={maxHeight}
        emptyMessage="No roles match the selected filters."
      />
    );

  const filterPopover = (
    <Popover
      open={Boolean(filterAnchorEl)}
      anchorEl={filterAnchorEl}
      onClose={closeFilters}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{
        sx: {
          background: POPOVER.background,
          backdropFilter: POPOVER.backdropFilter,
          border: POPOVER.border,
          borderRadius: POPOVER.borderRadius,
          p: 2.5,
        },
      }}
    >
      <JobTitlesFilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onClose={closeFilters}
        countries={availableCountries}
        selectedPresetKey={selectedPresetKey}
      />
    </Popover>
  );

  const paginationControls = (
    <VuiPaginationControls
      currentPage={page}
      totalPages={totalPages}
      totalElements={totalElements}
      pageSize={pageSize}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      pageSizeOptions={[10, 25, 50]}
      isLoading={loading}
    />
  );

  return (
    <>
      <GlassCard>
        <TableHeaderWithFilters
          title="Top Job Titles"
          tooltipText="Most frequently posted job titles, normalised to remove location noise. Enriched with department, applicant volume, and primary hiring location."
          onFilterClick={openFilters}
          onExpandClick={() => setExpandOpen(true)}
          hasActiveFilters={hasActiveFilters}
          filtersAnchorEl={filterAnchorEl}
        />

        {filterPopover}

        {tableContent(pagedRows, "360px")}

        {!loading && totalElements > 0 && paginationControls}
      </GlassCard>

      {/* Expand modal — matches digital-presence pattern */}
      <Modal open={expandOpen} onClose={() => setExpandOpen(false)}>
        <Paper sx={MODAL_STYLE}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <VuiTypography variant="h6" color="white" fontWeight="medium">
              Top Job Titles
            </VuiTypography>
            <VuiBox>
              <IconButton onClick={openFilters} size="small" sx={{ color: "#c8cfca", mr: 0.5 }}>
                <FilterListIcon />
              </IconButton>
              <IconButton onClick={() => setExpandOpen(false)} size="small">
                <CloseIcon sx={{ color: "#c8cfca" }} />
              </IconButton>
            </VuiBox>
          </VuiBox>

          {filterPopover}

          <VuiBox sx={{ flexGrow: 1, overflowY: "auto", display: "flex", flexDirection: "column", minHeight: 0 }}>
            {tableContent(pagedRows, "fill")}
          </VuiBox>

          {!loading && totalElements > 0 && paginationControls}
        </Paper>
      </Modal>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function ExpansionTrends() {
  const { competitors, selectedCompetitor, selectedPresetKey } = useAnalytics();

  useEffect(() => {
    analyticsService.trackPageView("EXPANSION_TRENDS", {
      competitorId: selectedCompetitor,
      presetKey: selectedPresetKey,
    });
  }, [selectedCompetitor, selectedPresetKey]);

  const {
    trends,
    intelligence,
    marketVelocity,
    loading,
    intelligenceLoading,
    marketVelocityLoading,
    error,
    intelligenceError,
  } = useExpansionTrendsData();

  const isMarketView = selectedCompetitor === MARKET_TRENDS_ID;

  const competitorName = useMemo(() => {
    if (isMarketView) return "The market";
    return competitors.find((c) => c.id === selectedCompetitor)?.name || "This competitor";
  }, [selectedCompetitor, competitors, isMarketView]);

  // Exclude OTHER from charts and ministat — kept in the Job Titles table via the API.
  const functionBreakdownFiltered = useMemo(
    () => (trends.functionBreakdown || []).filter((d) => d.label !== "OTHER"),
    [trends.functionBreakdown]
  );
  const functionLabels  = functionBreakdownFiltered.map((d) => FUNCTION_LABELS[d.label] || d.label);
  const functionValues  = functionBreakdownFiltered.map((d) => d.count);

  const seniorityLabels = trends.seniorityBreakdown.map((d) => SENIORITY_LABELS[d.label] || d.label);
  const seniorityValues = trends.seniorityBreakdown.map((d) => d.count);

  const workTypeLabels  = trends.workTypeBreakdown.map((d) => d.label);
  const workTypeValues  = trends.workTypeBreakdown.map((d) => d.count);

  const locationCats    = trends.locationBreakdown.map((d) => d.label);
  const locationValues  = trends.locationBreakdown.map((d) => d.count);

  const velocityCats    = trends.hiringVelocity.map((d) => d.month);
  const velocityValues  = trends.hiringVelocity.map((d) => d.count);

  // topFunction derives from the filtered array — "Other" can never appear here.
  const topFunction  = functionLabels[0] || "—";
  const topSeniority = SENIORITY_LABELS[trends.seniorityBreakdown[0]?.label] || "—";

  const insightsLabel = isMarketView ? "Market Intelligence" : "Expansion Insights";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox>

        {/* ── KPI strip ── */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={3}>
            {loading ? <MiniStatCardSkeleton /> : (
              <MiniStatCard
                title={{ fontWeight: "medium", text: "Open Positions" }}
                count={trends.totalJobs.toLocaleString()}
                icon={{ component: <IoDocumentText size="22px" color="white" /> }}
              />
            )}
          </Grid>
          <Grid item xs={6} sm={3}>
            {loading ? <MiniStatCardSkeleton /> : (
              <MiniStatCard
                title={{ fontWeight: "medium", text: "Hiring Countries" }}
                count={trends.activeLocations.toLocaleString()}
                icon={{ component: <IoLocation size="22px" color="white" /> }}
              />
            )}
          </Grid>
          <Grid item xs={6} sm={3}>
            {loading ? <MiniStatCardSkeleton /> : (
              <MiniStatCard
                title={{ fontWeight: "medium", text: "Top Department" }}
                count={topFunction}
                icon={{ component: <IoBriefcase size="22px" color="white" /> }}
              />
            )}
          </Grid>
          <Grid item xs={6} sm={3}>
            {loading ? <MiniStatCardSkeleton /> : (
              <MiniStatCard
                title={{ fontWeight: "medium", text: "Seniority Focus" }}
                count={topSeniority}
                percentage={
                  trends.seniorityBreakdown[0]
                    ? { color: "success", text: `${trends.seniorityBreakdown[0].count} postings` }
                    : undefined
                }
                icon={{ component: <IoPeople size="22px" color="white" /> }}
              />
            )}
          </Grid>
        </Grid>

        {/* ── Expansion / Market Insights (pre-computed LLM) ── */}
        <ExpansionInsightsCard
          intelligence={intelligence}
          loading={intelligenceLoading}
          error={intelligenceError}
          label={insightsLabel}
        />

        {error && !loading && (
          <VuiBox sx={{ p: 3, textAlign: "center" }}>
            <VuiTypography variant="body2" color="error">
              Could not load hiring data. Please try again.
            </VuiTypography>
          </VuiBox>
        )}

        {!error && (
          <>
            {/* ── Market Trends: stacked velocity chart ── */}
            {isMarketView && (
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12}>
                  <ChartCard
                    title="Hiring Volume by Competitor"
                    tooltipText="Monthly job postings stacked by competitor, revealing relative hiring intensity and market share of open positions over time."
                    loading={marketVelocityLoading}
                    hasValues={!!(marketVelocity?.competitors?.length)}
                    competitorName="The market"
                    height="320px"
                  >
                    <MarketVelocityChart
                      velocityData={marketVelocity}
                      loading={false}
                      height="320px"
                    />
                  </ChartCard>
                </Grid>
              </Grid>
            )}

            {/* ── Row 1: Function donut + Monthly velocity ── */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} md={5}>
                <ChartCard
                  title="Hiring by Department"
                  tooltipText="Share of open roles across business functions."
                  loading={loading}
                  hasValues={functionValues.length > 0}
                  competitorName={competitorName}
                >
                  <VuiBox height="300px">
                    <Chart key={JSON.stringify(functionValues)} options={donutOptions(functionLabels, FUNCTION_COLORS)} series={functionValues} type="donut" width="100%" height="100%" />
                  </VuiBox>
                </ChartCard>
              </Grid>
              <Grid item xs={12} md={7}>
                <ChartCard
                  title="Posting Date Distribution"
                  tooltipText="Shows when currently active job postings were originally published. Older months appear lower because filled roles expire from LinkedIn over time — this is not a month-over-month hiring acceleration chart."
                  loading={loading}
                  hasValues={velocityValues.length > 0}
                  competitorName={competitorName}
                >
                  <VuiBox height="300px">
                    <Chart key={JSON.stringify(velocityValues)} options={velocityBarOptions(velocityCats)} series={[{ name: "Active Postings", data: velocityValues }]} type="bar" width="100%" height="100%" />
                  </VuiBox>
                </ChartCard>
              </Grid>
            </Grid>

            {/* ── Row 2: Locations + Work model + Seniority ── */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} md={6}>
                <ChartCard
                  title="Top Hiring Countries"
                  tooltipText="Countries with the highest concentration of active job postings."
                  loading={loading}
                  hasValues={locationValues.length > 0}
                  competitorName={competitorName}
                >
                  <VuiBox height="300px">
                    <Chart key={JSON.stringify(locationValues)} options={horizontalBarOptions(locationCats)} series={[{ name: "Postings", data: locationValues }]} type="bar" width="100%" height="100%" />
                  </VuiBox>
                </ChartCard>
              </Grid>

              <Grid item xs={12} md={3}>
                <ChartCard
                  title="Work Model"
                  tooltipText="Split between on-site, hybrid, and remote positions."
                  loading={loading}
                  hasValues={workTypeValues.length > 0}
                  competitorName={competitorName}
                >
                  <VuiBox height="300px">
                    <Chart key={JSON.stringify(workTypeValues)} options={donutOptions(workTypeLabels, WORKTYPE_COLORS)} series={workTypeValues} type="donut" width="100%" height={270} />
                  </VuiBox>
                </ChartCard>
              </Grid>

              <Grid item xs={12} md={3}>
                <ChartCard
                  title="Seniority Mix"
                  tooltipText="Distribution of open roles by seniority level."
                  loading={loading}
                  hasValues={seniorityValues.length > 0}
                  competitorName={competitorName}
                >
                  <VuiBox height="300px">
                    <Chart key={JSON.stringify(seniorityValues)} options={donutOptions(seniorityLabels, SENIORITY_COLORS)} series={seniorityValues} type="donut" width="100%" height={270} />
                  </VuiBox>
                </ChartCard>
              </Grid>
            </Grid>

            {/* ── Job Titles table ── */}
            <JobTitlesTable
              rows={trends.topTitles}
              loading={loading}
              selectedPresetKey={selectedPresetKey}
              competitorName={competitorName}
            />
          </>
        )}
      </VuiBox>
    </DashboardLayout>
  );
}

export default ExpansionTrends;
